import React, { useEffect, useRef } from "react";
import type { Clip } from "@hudhud/core";
import { Icon } from "../../components/Icon.js";
import { useEditor } from "../store.js";
import { liveLevels } from "../levels.js";

/** The BG audio fader's percent for a level in dB (−40 dB = 0 %, −4 dB = 100 %), as the Sound tab shows it. */
export const bgPct = (db: number) => Math.round(((db + 40) / 36) * 100);
export const pctToDb = (pct: number) => Math.round(-40 + (pct / 100) * 36);

/**
 * A live meter: green up through amber to red at the top, lit from the bottom as far as the sound reaches this frame, with a thin
 * peak line that holds for a moment. Drawn straight onto the DOM every animation frame from the shared levels, never through React.
 */
const Meter: React.FC<{ which: "voice" | "bed"; label: string }> = ({ which, label }) => {
  const cover = useRef<HTMLElement>(null), peak = useRef<HTMLElement>(null), root = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    let raf = 0, shown = -1, shownPeak = -1, level = 0;
    const draw = () => {
      // the meter eases down between frames of news, and falls to nothing when the preview has been quiet for a moment
      const fresh = performance.now() - liveLevels.at < 400;
      const target = fresh ? liveLevels[which] : 0;
      level = target > level ? target : Math.max(0, level - 0.03);
      const pk = fresh ? liveLevels[which === "voice" ? "voicePeak" : "bedPeak"] : 0;
      if (Math.abs(level - shown) > 0.004 && cover.current) { shown = level; cover.current.style.height = `${(1 - level) * 100}%`; root.current?.setAttribute("data-level", level.toFixed(2)); }
      if (Math.abs(pk - shownPeak) > 0.004 && peak.current) { shownPeak = pk; peak.current.style.bottom = `${pk * 100}%`; peak.current.style.opacity = pk > 0.02 ? "1" : "0"; }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [which]);
  return <span ref={root} className="tl-meter" role="meter" aria-label={`${label} playing level`} data-testid={`meter-${which}`}><i ref={cover} className="cover" /><b ref={peak} className="peak" /></span>;
};

/** One upright fader with its meter: the mute on its icon at the top, the value under it. */
const Fader: React.FC<{ icon: string; label: string; which: "voice" | "bed"; value: number; min: number; max: number; text: string; muted: boolean; onChange: (v: number) => void; onMute: () => void; testId: string }> = ({ icon, label, which, value, min, max, text, muted, onChange, onMute, testId }) => {
  const commit = () => useEditor.getState().update((c) => c); // the drag was transient: one history step for the whole move
  return (
    <span className={`tl-fader${muted ? " muted" : ""}`} data-testid={testId} title={`${label}: ${muted ? "muted" : text} · the meter shows it playing · drag to change, tap the icon to mute`}>
      <button className="tl-btn" onClick={onMute} aria-pressed={muted} aria-label={`${muted ? "Unmute" : "Mute"} the ${label.toLowerCase()}`}><Icon name={icon} size={15} /></button>
      <span className="tl-fader-body">
        <Meter which={which} label={label} />
        <input type="range" min={min} max={max} step={1} value={value} aria-label={`${label} level`} aria-orientation="vertical" disabled={muted} onChange={(e) => onChange(Number(e.target.value))} onPointerUp={commit} onKeyUp={commit} onBlur={commit} style={{ ["--fill" as string]: `${((value - min) / (max - min)) * 100}%` }} />
      </span>
      <span className="num">{muted ? "off" : text}</span>
    </span>
  );
};

/**
 * The two faders beside the timeline: the speaker's voice (dB, −6 is the house level) and the BG audio bed (per cent), each with a
 * mute on its icon. The same settings as under Sound, within reach while trimming.
 */
export const Levels: React.FC<{ clip: Clip }> = ({ clip }) => {
  const st = useEditor.getState;
  const set = (fn: (c: Clip) => Clip) => st().update(fn, { transient: true });
  const bg = clip.music.trackId;
  return (
    <div className="tl-side" role="group" aria-label="Audio levels">
      <Fader icon="mic" label="Voice" which="voice" value={clip.voiceDb} min={-24} max={12} text={`${clip.voiceDb > 0 ? "+" : ""}${clip.voiceDb} dB`} muted={clip.voiceMuted} testId="level-voice"
        onChange={(v) => set((c) => ({ ...c, voiceDb: v }))} onMute={() => st().update((c) => ({ ...c, voiceMuted: !c.voiceMuted }))} />
      {bg ? <Fader icon="note" label="BG audio" which="bed" value={bgPct(clip.music.volumeDb)} min={0} max={100} text={`${bgPct(clip.music.volumeDb)}%`} muted={clip.music.muted} testId="level-bg"
        onChange={(v) => set((c) => ({ ...c, music: { ...c.music, volumeDb: pctToDb(v) } }))} onMute={() => st().update((c) => ({ ...c, music: { ...c.music, muted: !c.music.muted } }))} />
        : <span className="tl-fader empty"><button className="tl-btn" onClick={() => st().openSoundGallery("playhead")} title="No BG audio yet: pick a bed from the sound library" aria-label="Pick BG audio"><Icon name="note" size={15} /></button><span className="num">none</span></span>}
    </div>
  );
};
